/**
 * Desktop Layout Component
 *
 * Layout wrapper for desktop application pages using NavigationDesktop and FooterDesktop
 * from the component showcase. Provides consistent navigation and footer across all desktop routes.
 *
 * All orchestration logic lives in useLayoutOrchestration — this component is a pure
 * rendering shell. The same hook is consumed by MobileLayout.
 *
 * Shared overlays (sheets + alerts) are rendered by LayoutOverlays.
 */

import { ReactNode, useRef, useCallback } from 'react'
import {
  NavigationDesktop,
  FooterDesktop,
  Toast,
  useUIStore,
} from '@restaurant/core'
import { useLayoutOrchestration } from '../../hooks/useLayoutOrchestration'
import { LayoutOverlays } from './LayoutOverlays'
import { DebugBadge } from './DebugBadge'

interface DesktopLayoutProps {
  children: ReactNode
}

export function DesktopLayout({ children }: DesktopLayoutProps) {
  const o = useLayoutOrchestration()
  const { toastVisible, toastMessage, dismissToast } = useUIStore()

  const mainRef = useRef<HTMLDivElement>(null)
  const handleMainScroll = useCallback(() => {
    const main = mainRef.current
    if (!main) return
    o.handleScrollSpy(main)
  }, [o.handleScrollSpy])

  return (
    <div className="h-screen flex flex-col" style={{ backgroundColor: 'var(--theme-fill-page-background)' }}>
      {/* Desktop Navigation Header */}
      <NavigationDesktop
        selectedTabId={o.selectedTabId}
        onTabChange={o.handleTabChange}
        showOffers={o.showOffers}
        showRewards={o.showRewards}
        showActiveOrderButton={o.orderStatusToast.visible}
        checkoutButtonState={o.checkoutButtonState}
        checkoutTotal={o.checkoutTotal}
        onCheckoutClick={o.handleCheckoutClick}
        orderStatusToast={{
          title: o.orderStatusToast.title,
          subtitle: o.orderStatusToast.description,
          iconName: o.orderStatusToast.iconName,
          visible: o.orderStatusToast.visible,
          onToastClick: o.orderStatusToast.onToastClick,
        }}
        brandLogoUrl={o.currentRestaurant?.logoUrl}
        brandName={o.currentRestaurant?.name || "Restaurant"}
        toolbar={o.toolbar}
        testID="desktop-navigation"
      />

      {/* Main Content Area - Scrollable */}
      <main ref={mainRef} className="flex-1 flex flex-col overflow-y-auto" onScroll={handleMainScroll}>
        {children}
      </main>

      {/* Action toast notification */}
      {toastVisible && toastMessage && (
        <div className="shrink-0 flex justify-center" style={{ padding: '0 var(--layout-padding-page-horizontal) var(--layout-space-02)' }}>
          <Toast
            title={toastMessage}
            onPress={dismissToast}
            testID="action-toast"
          />
        </div>
      )}

      {/* Desktop Footer - Fixed at bottom */}
      <FooterDesktop
        location={o.currentLocation}
        selectedOrderType={o.orderType}
        multipleLocations={o.hasMultipleLocations}
        showChangeButton={o.orderType === 'Delivery' || o.hasMultipleLocations}
        locationBadge={o.isDebugMode ? <DebugBadge /> : undefined}
        onOrderTypeChange={o.handleOrderTypeChange}
        onChangeLocationPress={o.handleChangeLocationPress}
        testID="desktop-footer"
      />

      {/* Shared overlays: sheets + alerts */}
      <LayoutOverlays orchestration={o} />
    </div>
  )
}
